import { padStart } from 'lodash';
import timezones from '../constants/timezones.json';
import { ITimezone } from '../interfaces/timezone.interface';
import { DashboardDataFrequency } from '../interfaces/ws-dashboard.interface';
import { SharedCommonUtility } from './common.utility';

export class SharedDateUtility {
  public static DAY_IN_MS: number = 24 * 60 * 60 * 1000; // 24h * 60m * 60s * 1000ms to convert 1 day to ms
  public static DEFAULT_LOCALE: string = 'en-US';
  public static DAYS_PER_WEEK: number = 7;
  public static DAYS_PER_MONTH: number = 30;
  public static DAYS_PER_QUARTER: number = 91;

  /**
   * Normalize a date string into a date object.
   *
   * @param date May be an actual date object or a date string.
   * @return A date object
   * @private
   */
  private static getDateObject(date: string | Date): Date {
    if (typeof date === 'string') {
      return new Date(date);
    }
    return date;
  }

  public static isValidStringDate(date: string): boolean {
    if (typeof date !== 'string' || date.length === 0) {
      return false;
    }
    const time: number = Date.parse(date);

    return isNaN(time) === false;
  }

  /**
   * Checks whether a date string matches YYYY-MM-DD format, without time or timezone info.
   * @param date
   * @returns `true` if it matches YYYY-MM-DD, otherwise returns `false`.
   */
  public static isDateWithoutTime(date: string): boolean {
    return /^\d{4}-\d{2}-\d{2}$/.test(date);
  }

  public static isValidDate(dateValue: string | Date): boolean {
    if (typeof dateValue === 'string') {
      return SharedDateUtility.isValidStringDate(dateValue);
    }
    const isInvalidDate: boolean = isNaN(new Date(dateValue).getTime());
    return !isInvalidDate;
  }

  public static toMidnight(value: string | Date): Date {
    const dateValue: Date = new Date(value);
    return new Date(dateValue.getFullYear(), dateValue.getMonth(), dateValue.getDate());
  }

  public static toUTCMidnight(value: string | Date): Date {
    const dateValue: Date = new Date(value);
    const year: number = dateValue.getUTCFullYear();
    const month: number = dateValue.getUTCMonth();
    const day: number = dateValue.getUTCDate();
    return new Date(Date.UTC(year, month, day, 0, 0, 0));
  }

  public static getUTCDayRange(dateStr: Date | string): [Date, Date] {
    const date: Date = new Date(dateStr);
    const year: number = date.getUTCFullYear();
    const month: number = date.getUTCMonth();
    const day: number = date.getUTCDate();
    const startDate: Date = new Date(Date.UTC(year, month, day, 0, 0, 0));
    const endDate: Date = new Date(Date.UTC(year, month, day + 1, 0, 0, 0));
    return [startDate, endDate];
  }

  public static endOfUTCDay(value: Date | string): Date {
    const date: Date = new Date(value);
    return new Date(Date.UTC(date.getUTCFullYear(), date.getUTCMonth(), date.getUTCDate(), 23, 59, 59, 999));
  }

  public static isGreaterThanOrEqualUTCTodayMidnight(date: Date): boolean {
    const todayMidnight: Date = SharedDateUtility.toUTCMidnight(new Date());
    return date >= todayMidnight;
  }

  public static toNextMidnight(value: string | Date): Date {
    const nextDate: Date = new Date(value);
    nextDate.setDate(nextDate.getDate() + 1);
    nextDate.setHours(0, 0, 0, 0);
    return nextDate;
  }

  public static getLastDayOfMonth(value: Date): number {
    return new Date(value.getFullYear(), value.getMonth() + 1, 0).getDate();
  }

  public static getDateWithLastDayOfMonth(value: Date): Date {
    const lastDayOfMonth: number = SharedDateUtility.getLastDayOfMonth(value);
    const dateWithAdjustedDay: Date = new Date(value);
    dateWithAdjustedDay.setDate(lastDayOfMonth);
    return dateWithAdjustedDay;
  }

  public static formatTime(ms: number): string {
    const date: Date = new Date(ms);
    const hours: number = Math.floor(ms / 3_600_000); // getUTCHours() wraps at 24 — keep arithmetic
    const minutes: number = date.getUTCMinutes();
    const seconds: number = date.getUTCSeconds();
    const milliseconds: number = date.getUTCMilliseconds();

    if (hours === 0 && minutes === 0 && seconds === 0 && milliseconds === 0) {
      return '< 1ms';
    }

    let time: string = '';
    if (hours > 0) {
      time += hours + 'h ';
    }
    if (minutes > 0) {
      time += minutes + 'm ';
    }
    if (seconds > 0) {
      time += seconds + 's ';
    }
    if (milliseconds > 0) {
      time += milliseconds + 'ms';
    }

    return time.trim();
  }

  public static getRelativeDateString(date: string | Date): string {
    const otherDate: Date = this.getDateObject(date);
    const weekInMs = 7 * 24 * 3600 * 1000;
    if (new Date().getTime() - otherDate.getTime() < weekInMs) {
      return otherDate.toLocaleDateString('en-US', { weekday: 'long' });
    }
    return otherDate.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' });
  }

  // Example: June 1, 2021
  public static getDateString(date: string | Date): string {
    const otherDate: Date = this.getDateObject(date);
    return otherDate.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' });
  }

  /**
   * Format date into month and year
   * Example: January 2026
   *
   * @param date A date object or string
   * @returns The formatted date string
   */
  public static getMonthYearString(date: string | Date): string {
    const otherDate: Date = this.getDateObject(date);
    return otherDate.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
  }

  public static getAbbreviatedMonthYearString(date: string | Date): string {
    const otherDate: Date = this.getDateObject(date);
    return otherDate.toLocaleDateString('en-US', { month: 'short', year: 'numeric' });
  }

  /**
   * Format date into abbreviated month and day, without a year.
   * Example: Sep 28
   *
   * Defaults to UTC so callers formatting from UTC calendar boundaries (e.g. an interval's
   * end-of-day marker) render the same calendar day regardless of the host runtime's local
   * timezone.
   *
   * @param date A date object or string
   * @param timeZone The IANA timezone to format in. Defaults to 'UTC'.
   * @returns The formatted date string
   */
  public static getAbbreviatedMonthDayString(date: string | Date, timeZone: string = 'UTC'): string {
    const otherDate: Date = this.getDateObject(date);
    return otherDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric', timeZone });
  }

  public static getNextYear(date: Date): Date {
    const nextYearDate: Date = new Date(date);
    nextYearDate.setFullYear(nextYearDate.getFullYear() + 1);
    return nextYearDate;
  }

  /**
   * Check if the given epoch date has already occurred
   *
   * @param epochDate The epoch date to be verified
   * @returns A boolean indicating whether the given epoch date has already occurred
   */
  public static checkIfEpochDateAlreadyOccurred(epochDate: number): boolean {
    return Date.now() >= epochDate;
  }

  /**
   * Get the date from the given last milliseconds ago (before now)
   *
   * @param msTimeAgo Time ago in milliseconds
   * @returns Date object of the date occurred in the given last milliseconds ago
   */
  public static getLastMSDate(msTimeAgo: number): Date {
    return new Date(Date.now() - msTimeAgo);
  }

  public static getReportDateString(date: Date | string, timezoneOffset?: number): string {
    const options: Intl.DateTimeFormatOptions = { year: 'numeric', month: '2-digit', day: '2-digit' };
    if (timezoneOffset) {
      options.timeZone = SharedDateUtility.getNamedTimezone(timezoneOffset);
    }
    return new Date(date).toLocaleDateString('en-US', options);
  }

  public static addSeconds(prevDate: Date, secondsToAdd: number): Date {
    const result: Date = new Date(prevDate.getTime());
    result.setSeconds(prevDate.getSeconds() + secondsToAdd);
    return result;
  }

  public static addDays(originalData: Date, daysToAdd: number): Date {
    return this.addSeconds(originalData, daysToAdd * 24 * 60 * 60);
  }

  // Uses a fixed millisecond offset rather than addDays()/setSeconds() (which operate in local time
  // and can drift by an hour across a DST boundary outside TZ=UTC) — a week is an unambiguous fixed
  // duration, so this stays timezone-agnostic like addMonths().
  public static addWeeks(date: Date, weeks: number): Date {
    return new Date(date.getTime() + weeks * SharedDateUtility.DAYS_PER_WEEK * SharedDateUtility.DAY_IN_MS);
  }

  public static getDaysBetween(start: Date, end: Date): number {
    return (end.getTime() - start.getTime()) / SharedDateUtility.DAY_IN_MS;
  }

  public static computeDataTimeframe(totalDays: number): { startDate: Date; endDate: Date } {
    const endDate = new Date();
    const startDate = SharedDateUtility.addDays(endDate, -totalDays);
    return { startDate, endDate };
  }

  public static utcYesterdayMidnight(now: Date = new Date()): Date {
    return new Date(SharedDateUtility.toUTCMidnight(now).getTime() - SharedDateUtility.DAY_IN_MS);
  }

  /**
   * Compute a data timeframe anchored at "now" by walking back the given number of whole
   * calendar periods, so the legacy snapshot-count path resolves to the same window the
   * timeframe picker produces for the same selection.
   */
  public static computeDataTimeframeForFrequency(
    frequency: DashboardDataFrequency,
    timeframe: number,
    now: Date = new Date(),
  ): { startDate: Date; endDate: Date } {
    const endDate: Date = SharedDateUtility.utcYesterdayMidnight(now);
    const candidate: Date = SharedDateUtility.addPeriods(endDate, frequency, -timeframe);
    const startDate: Date =
      SharedDateUtility.addPeriods(candidate, frequency, timeframe) < endDate
        ? SharedDateUtility.addDays(candidate, 1)
        : candidate;
    return { startDate, endDate };
  }

  /**
   * Formats a date range for display. Shows only month names when both dates are in
   * the same year; otherwise includes the year for the start date too.
   *
   * Example (same year): "January - March 2026"
   * Example (different year): "November 2025 - February 2026"
   */
  public static formatReportDateRange(startDate: Date, endDate: Date): string {
    const startYear: number = startDate.getFullYear();
    const endYear: number = endDate.getFullYear();
    const startDateStr: string =
      startYear === endYear
        ? startDate.toLocaleDateString(SharedDateUtility.DEFAULT_LOCALE, { month: 'long' })
        : SharedDateUtility.getMonthYearString(startDate);
    const endDateStr: string = SharedDateUtility.getMonthYearString(endDate);
    return `${startDateStr} - ${endDateStr}`;
  }

  public static formatCalendarDateRange(startDate: Date, endDate: Date): string {
    return `${SharedDateUtility.getDateString(startDate)} – ${SharedDateUtility.getDateString(endDate)}`;
  }

  /**
   * Normalize a date to its UTC calendar day as a local Date, for display without timezone drift.
   */
  public static toUTCCalendarDate(date: Date | string): Date {
    return SharedDateUtility.getUTCDateWithoutTime(SharedDateUtility.getUTCISODate(date));
  }

  /** Format a UTC calendar day for display, e.g. "March 23, 2026". */
  public static formatUTCCalendarDate(date: Date | string): string {
    return SharedDateUtility.getDateString(SharedDateUtility.toUTCCalendarDate(date));
  }

  public static formatUTCCalendarDateRange(startDate: Date | string, endDate: Date | string): string {
    return SharedDateUtility.formatCalendarDateRange(
      SharedDateUtility.toUTCCalendarDate(startDate),
      SharedDateUtility.toUTCCalendarDate(endDate),
    );
  }

  public static isYesterday(date: Date): boolean {
    const yesterday: Date = new Date(new Date().getFullYear(), new Date().getMonth(), new Date().getDate() - 1);
    return date.toLocaleDateString() === yesterday.toLocaleDateString();
  }

  public static isTomorrow(date: Date): boolean {
    const tomorrow: Date = new Date(new Date().getFullYear(), new Date().getMonth(), new Date().getDate() + 1);
    return date.toLocaleDateString() === tomorrow.toLocaleDateString();
  }

  public static isToday(date: Date): boolean {
    return date.toLocaleDateString() === new Date().toLocaleDateString();
  }

  /**
   * Format date in international standard format YYYY-MM-DD (ISO8601)
   * using local time zone
   * Example: 2021-07-20
   *
   * Timezone contract: this reads the LOCAL calendar day (`getDate()/getMonth()/getFullYear()`).
   * It only round-trips a date-only (`YYYY-MM-DD`) string back to itself if that string was
   * parsed with `SharedDateUtility.getLocalDate()` — never with a bare `new Date(dateOnlyString)`.
   * A bare `new Date(...)` parses date-only strings as UTC midnight, so reading it back here
   * silently loses a day in any timezone with a nonzero UTC offset. If the string instead came
   * from a UTC-based source (e.g. `SharedDateUtility.getUTCISODate()`), read it back with
   * `getUTCISODate()` too, not this method — don't mix the local pair with the UTC pair.
   *
   * @param date A date object or string
   */
  public static getLocalISODate(date: Date | string): string {
    const otherDate: Date = this.getDateObject(date);
    const padNumber = (n: number): string => n.toString().padStart(2, '0');

    const day = padNumber(otherDate.getDate());
    const month = padNumber(otherDate.getMonth() + 1);
    const year = otherDate.getFullYear();

    return `${year}-${month}-${day}`;
  }

  public static dateToDatetimeLocalFormat(date: Date): string {
    return (
      SharedCommonUtility.withLeadingZeros(date.getFullYear(), 4) +
      '-' +
      SharedCommonUtility.withLeadingZeros(date.getMonth() + 1, 2) +
      '-' +
      SharedCommonUtility.withLeadingZeros(date.getDate(), 2) +
      'T' +
      SharedCommonUtility.withLeadingZeros(date.getHours(), 2) +
      ':' +
      SharedCommonUtility.withLeadingZeros(date.getMinutes(), 2)
    );
  }

  public static amountOfDaysInMonth(month: number, year: number): number {
    let dayNum: number;
    if ([1, 3, 5, 7, 8, 10, 12].includes(month)) {
      return 31;
    }
    if ([4, 6, 9, 11].includes(month)) {
      return 30;
    }
    // If month is February, calculate whether it is a leap year or not
    const isLeapYear = new Date(year, 1, 29).getMonth() === 1;
    if (isLeapYear) {
      dayNum = 29;
    } else {
      dayNum = 28;
    }
    return dayNum;
  }

  public static getLocalDateFromNumbers(year: number, month: number, date: number): Date {
    const utc: number = Date.UTC(year, month, date);
    return new Date(utc + new Date().getTimezoneOffset() * 60000);
  }

  /**
   * Receives a date in the format 2023-02-01T00:03:03.935Z and returns 2023-02-01, ruling out timezone differences when they don't matter.
   * If time matters, use `SharedDateUtility.getLocalDate` instead.
   * @param inputISODate
   * @returns date without time
   */
  public static getUTCDateWithoutTime(inputISODate: string): Date {
    return SharedDateUtility.getLocalDate(inputISODate.split('T')[0]);
  }

  /**
   * Parses a date/datetime string into a `Date` whose LOCAL calendar fields match the input.
   * For a date-only (`YYYY-MM-DD`) input, this is the required counterpart to
   * `SharedDateUtility.getLocalISODate()` — pair them so a date-only string survives
   * `getLocalISODate(getLocalDate(x)) === x` in any timezone. Do NOT substitute a bare
   * `new Date(dateOnlyString)` here: that parses as UTC midnight, and reading it back via
   * `getLocalISODate()` (or any local-field getter) will silently shift it back a day in any
   * timezone with a nonzero UTC offset.
   */
  public static getLocalDate(inputDate: string): Date {
    const date: Date = new Date(inputDate);
    if (SharedDateUtility.isDateWithoutTime(inputDate) === false) {
      return date;
    }

    return new Date(date.getTime() + date.getTimezoneOffset() * 60000);
  }

  public static getTimezoneTextByValue(timezoneValue: string): string {
    if (typeof timezoneValue !== 'string') {
      return null;
    }

    const timezone = (_timezone: ITimezone): boolean => {
      return _timezone.value === timezoneValue;
    };

    const foundedTimezone: ITimezone = (timezones as unknown as ITimezone[]).find(timezone);

    return foundedTimezone ? foundedTimezone.text : null;
  }

  public static convertMinutestoMilliseconds(minutes: number): number {
    return Math.floor(minutes * 60 * 1000);
  }

  public static getNamedTimezone(timezoneOffset: number): string {
    const timeZoneNotDetermined: string = 'UTC';

    for (const tz of timezones) {
      if (-tz.offset === timezoneOffset) {
        return tz.utc[0];
      }
    }

    return timeZoneNotDetermined;
  }

  public static getLocaleDateTimeWithDayName(date: string, locale: string): string {
    const d: Date = new Date(date);

    return (
      d.toLocaleDateString(locale, { weekday: 'long' }) + ', ' + d.toLocaleDateString(locale) + ' ' + d.toLocaleTimeString(locale)
    );
  }

  /**
   * Format date as "Monday, March 24, 2026 at 14:32 UTC" with day name, date, 24h time, and UTC timezone.
   *
   * @param date A date object or string
   * @returns The formatted date string with day name, month, day, year, time in 24h format, and UTC timezone
   */
  public static getUTCDateTimeWithDayName(date: Date | string): string {
    const d: Date = this.getDateObject(date);
    const options: Intl.DateTimeFormatOptions = {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      timeZone: 'UTC',
      hour12: false,
    };
    const formatted = d.toLocaleDateString('en-US', options);
    return `${formatted} UTC`.replace(/, (\d{1,2}):(\d{2})/, ' at $1:$2');
  }

  public static getLocaleDateTime(
    date: Date,
    userLocale: string = SharedDateUtility.DEFAULT_LOCALE,
    userTimezoneOffset: number = 0,
    skipTime: boolean = false,
  ): string {
    const utcTimeZone: string = SharedDateUtility.getNamedTimezone(userTimezoneOffset ?? 0);
    let timeFormat: Intl.DateTimeFormatOptions;
    if (!skipTime) {
      timeFormat = {
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
        timeZoneName: 'short',
      };
    }

    const format: Intl.DateTimeFormatOptions = {
      timeZone: utcTimeZone,
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      ...timeFormat,
    };

    return date.toLocaleString(userLocale || SharedDateUtility.DEFAULT_LOCALE, format);
  }

  public static getGMTDateTime(
    date: Date,
    userLocale: string = SharedDateUtility.DEFAULT_LOCALE,
    timezoneOffset: number = 0,
  ): string {
    // not using getNamedTimezone() as it assumes only -ve offsets
    const timeZone: string =
      timezones.find((tz: { offset: number; utc: string[] }) => tz.offset === (timezoneOffset ?? 0))?.utc[0] || 'UTC';

    const format: { [key: string]: string } = {
      timeZone: timeZone,
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      timeZoneName: 'short',
    };

    const formatter = Intl.DateTimeFormat(userLocale || SharedDateUtility.DEFAULT_LOCALE, format);
    return formatter.format(date);
  }

  public static getLocaleDate(date: Date, userLocale: string, timeZone: string): string {
    return date.toLocaleDateString(userLocale, { timeZone });
  }

  public static getFormattedDateForDownload(date: Date): string {
    return `${date.getFullYear()}_${
      date.getMonth() + 1
    }_${date.getDate()}_${date.getHours()}_${date.getMinutes()}_${date.getSeconds()}`;
  }

  /**
   * @returns only the date portion of ISO date without time. e.g. '2022-03-29' date based on UTC
   *
   * Timezone contract: this reads the UTC calendar day. It's the UTC counterpart to
   * `SharedDateUtility.getLocalDate()`/`getLocalISODate()` — don't mix the two pairs. If a
   * date-only string round-trips through `new Date(...)` (which parses as UTC midnight) and is
   * read back here, that's consistent; but if it's read back with `getLocalISODate()` instead,
   * it will silently shift by a day in any timezone with a nonzero UTC offset.
   */
  public static getUTCISODate(date: Date | string): string {
    if (SharedCommonUtility.isNullish(date)) {
      return null;
    }

    const dateString: string = new Date(date).toISOString();
    return dateString.substring(0, 10);
  }

  // For toronto, return something like, '2022-07-28T14:04:00.000-0400'
  public static toJiraDateTimeString(date: Date): string {
    const tzo: number = -date.getTimezoneOffset();
    const dif: string = tzo >= 0 ? '+' : '-';

    function pad(num: number): string {
      return padStart(String(num), 2, '0');
    }

    return (
      date.getFullYear() +
      '-' +
      pad(date.getMonth() + 1) +
      '-' +
      pad(date.getDate()) +
      'T' +
      pad(date.getHours()) +
      ':' +
      pad(date.getMinutes()) +
      ':' +
      pad(date.getSeconds()) +
      '.000' +
      dif +
      pad(Math.floor(Math.abs(tzo) / 60)) +
      pad(Math.abs(tzo) % 60)
    );
  }

  /**
   * Format a date as a long date with time in local timezone.
   *
   * @param date The date to format
   * @param timeStyle `'short'` omits seconds (default): "February 10, 2021 at 7:06 PM"
   *                  `'medium'` includes seconds: "February 10, 2021 at 7:06:30 PM"
   */
  public static getDateAtTime(date: Date, timeStyle: 'short' | 'medium' = 'short'): string {
    return Intl.DateTimeFormat('en-US', { dateStyle: 'long', timeStyle } as any).format(date);
  }

  /**
   * Randomizes time within a day and sets the same time to both
   * startDate and endDate
   *
   * @param startDate
   * @param endDate
   */
  public static randomizeTime(startDate: Date, endDate?: Date): void {
    function getRandomInt(maxExclusive: number): number {
      return Math.floor(Math.random() * maxExclusive);
    }
    const hourMinSec: [number, number, number] = [getRandomInt(24), getRandomInt(60), getRandomInt(60)];
    startDate.setHours(...hourMinSec);
    if (SharedCommonUtility.notNullish(endDate)) {
      endDate.setHours(...hourMinSec);
    }
  }

  public static IsDateGreaterThanOrEqualToday(date: Date): boolean {
    return SharedDateUtility.isToday(date) || date >= new Date();
  }

  /**
   * Retrieves the date from the input field, supposing it was entered as
   * a local timezone.
   *
   * @param dateControl the date form input control
   */
  public static getDateFromLocalInputDate(value: string): Date {
    if (SharedCommonUtility.isNullish(value)) {
      return null;
    }
    const date: Date = new Date(value); // Here date will be parsed in UTC
    const year: number = date.getUTCFullYear();
    const month: number = date.getUTCMonth();
    const day: number = date.getUTCDate();

    date.setFullYear(year, month, day); // Set year, month, date as local timezone
    date.setHours(0, 0, 0, 0); // Set at midnight local time, but may be could use current local time?

    return date;
  }

  /**
   * Retrieves the quarter from given date
   * Jan-Mar: 1
   * Apr-Jun: 2
   * Jul-Sep: 3
   * Oct-Dec: 4
   *
   * @param date input date
   */
  public static getQuarter(date: Date): number {
    return Math.floor((date.getUTCMonth() + 3) / 3);
  }

  public static getTimeSinceInMinutesHoursDays(lastDate: Date): string {
    const timeDiff: number = new Date().getTime() - lastDate.getTime();
    if (timeDiff < 1000 * 60 * 60) {
      // less than 1 hour
      return `${Math.floor(new Date(timeDiff).getUTCMinutes())} minutes`;
    } else if (new Date().getTime() - lastDate.getTime() < 1000 * 60 * 60 * 24) {
      // less than 1 day
      return `${Math.floor(new Date(timeDiff).getUTCHours())} hours`;
    }
    // more than 1 day
    return `${Math.floor(new Date(timeDiff).getTime() / (1000 * 60 * 60 * 24))} days`;
  }

  public static getWeekStartUTC(input: string | Date): Date {
    const date = new Date(input);
    const dayOfWeek = date.getUTCDay();
    const year = date.getUTCFullYear();
    const month = date.getUTCMonth();
    const day = date.getUTCDate() - dayOfWeek;
    return new Date(Date.UTC(year, month, day, 0, 0, 0, 0));
  }

  public static getMonthStartUTC(input: string | Date): Date {
    const date = new Date(input);
    const year = date.getUTCFullYear();
    const month = date.getUTCMonth();
    return new Date(Date.UTC(year, month, 1, 0, 0, 0, 0));
  }

  public static getQuarterStartUTC(input: string | Date): Date {
    const date = new Date(input);
    const year = date.getUTCFullYear();
    const month = date.getUTCMonth();
    const quarterStartMonth = Math.floor(month / 3) * 3;
    return new Date(Date.UTC(year, quarterStartMonth, 1, 0, 0, 0, 0));
  }

  /**
   * Add N calendar months to a date, clamping the day to the last day of the target month.
   * e.g. Jan 31 + 1 month → Feb 28 (not Feb 31, which would overflow to Mar 3).
   */
  public static addMonths(date: Date, months: number): Date {
    const result = new Date(date);
    const originalDay = result.getUTCDate();
    result.setUTCMonth(result.getUTCMonth() + months, 1); // set to 1st to avoid overflow
    const lastDay = new Date(Date.UTC(result.getUTCFullYear(), result.getUTCMonth() + 1, 0)).getUTCDate();
    result.setUTCDate(Math.min(originalDay, lastDay));
    return result;
  }

  /**
   * Shifts a date by N whole periods of the given frequency, using calendar arithmetic on UTC
   * fields: weekly -> addWeeks(n), monthly -> addMonths(n), quarterly -> addMonths(n * 3) — a
   * quarter is three calendar months, not a fixed `DAYS_PER_QUARTER` window. Negative `periods`
   * shifts backwards. Day-of-month is clamped to the target month's length (Jan 31 + 1 month ->
   * Feb 28), inheriting addMonths()' behaviour.
   */
  public static addPeriods(date: Date, frequency: DashboardDataFrequency, periods: number): Date {
    if (frequency === DashboardDataFrequency.weekly) {
      return SharedDateUtility.addWeeks(date, periods);
    }
    if (frequency === DashboardDataFrequency.quarterly) {
      return SharedDateUtility.addMonths(date, periods * 3);
    }
    return SharedDateUtility.addMonths(date, periods);
  }
}
